<!--
  ZoneTable part — the footer: the "Showing x-y of z" summary, the per-page select, and the page
  strip. INTERNAL: not a Zone component, no story, not in llms.txt. Rendered only by Table.vue.

  Presentational. It holds no state of its own: the page and per-page it displays arrive as props
  and it asks for changes through events, so the table stays the single owner of query state and
  the single emitter of `update:page` / `update:perPage` to the caller.
-->

<template>
  <!--
    Below the tablet breakpoint (`md`, 768px) this is Figma's SIMPLE pagination: the summary and
    Previous / Next, no page strip — the numbered list does not fit a phone-width card.
  -->
  <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-gray-200 px-3 py-2.5">
    <div class="flex items-center gap-2">
      <span
        class="text-sm text-gray-500"
        data-testid="pagination-summary"
      >{{ summaryText }}</span>
      <template v-if="totalItems > 0 && perPageOptions.length">
        <div
          class="w-[76px]"
          data-testid="per-page"
        >
          <ZoneSelect
            size="sm"
            :model-value="perPage"
            :options="perPageOptionItems"
            @update:model-value="$emit('update:perPage', $event)"
          />
        </div>
        <span class="text-sm text-gray-500">{{ t('per_page') }}</span>
      </template>
    </div>

    <PaginationRoot
      v-if="pageCount > 1"
      :page="page"
      :items-per-page="perPage"
      :total="totalItems"
      :sibling-count="1"
      show-edges
      class="flex items-center gap-1"
      @update:page="$emit('update:page', $event)"
    >
      <PaginationPrev
        v-if="page > 1"
        class="inline-flex h-8 items-center gap-1 rounded-md px-2 text-sm text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <ZoneIcon icon="chevron_left" />
        <span>{{ t('previous') }}</span>
      </PaginationPrev>

      <PaginationList
        v-slot="{ items }"
        class="tw-hidden items-center gap-1 md:flex"
      >
        <template
          v-for="(item, itemIndex) in items"
          :key="item.type === 'page' ? `page-${item.value}` : `ellipsis-${itemIndex}`"
        >
          <PaginationListItem
            v-if="item.type === 'page'"
            :value="item.value"
            :class="pageItemClasses(item.value)"
          >
            {{ item.value }}
          </PaginationListItem>
          <PaginationEllipsis
            v-else
            class="px-1 text-sm text-gray-400"
          >
            …
          </PaginationEllipsis>
        </template>
      </PaginationList>

      <PaginationNext
        v-if="page < pageCount"
        class="inline-flex h-8 items-center gap-1 rounded-md px-2 text-sm text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span>{{ t('next') }}</span>
        <ZoneIcon icon="chevron_right" />
      </PaginationNext>
    </PaginationRoot>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  PaginationRoot,
  PaginationList,
  PaginationListItem,
  PaginationPrev,
  PaginationNext,
  PaginationEllipsis,
} from 'reka-ui';
import ZoneIcon from '../Icon.vue';
import ZoneSelect from '../Select.vue';
import { formatCount } from './query';

defineOptions({ name: 'ZoneTablePagination' });

const { t } = useI18n();

const props = defineProps({
  page: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    required: true,
  },
  perPageOptions: {
    type: Array as PropType<number[]>,
    required: true,
  },
  pageCount: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  /** Plural noun for the summary line ("… of 40 members"). */
  itemsLabel: {
    type: String,
    required: true,
  },
});

defineEmits(['update:page', 'update:perPage']);

// The CURRENT perPage is folded in, so a caller whose `perPage` is not one of `perPageOptions`
// (a value restored from a URL, say) still sees its own number in the select rather than a
// blank trigger.
const perPageOptionItems = computed<{ id: number; name: string }[]>(() => {
  const values = [...new Set([...props.perPageOptions, props.perPage])].sort((a, b) => a - b);

  return values.map((value) => ({ id: value, name: String(value) }));
});

/**
 * Grouped with the same separators as every other count in the table — one of them reading
 * "4821" while the other reads "4,821" looks like a bug.
 */
const summaryText = computed<string>(() => {
  if (!props.totalItems) {
    return t('summary_empty', { items: props.itemsLabel });
  }

  const from = (props.page - 1) * props.perPage + 1;
  const to = Math.min(props.page * props.perPage, props.totalItems);

  return t('summary', { from: formatCount(from), to: formatCount(to), total: formatCount(props.totalItems), items: props.itemsLabel });
});

/**
 * Ported from Zone::Pagination#page_button: the active page is a primary-50 chip with a
 * primary-700 label AND a short bar tucked under it, drawn as an ::after so it needs no extra
 * element and cannot shift the row's height.
 */
function pageItemClasses(value: number): string {
  const active = value === props.page;

  return [
    'relative inline-flex h-8 min-w-8 items-center justify-center overflow-hidden rounded px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    "after:absolute after:-bottom-[3px] after:left-1/2 after:h-[6px] after:w-6 after:-translate-x-1/2 after:rounded-t-full after:bg-primary-700",
    active
      ? 'bg-primary-50 text-primary-700 after:block'
      : 'text-gray-500 hover:bg-gray-100 after:hidden',
  ].join(' ');
}
</script>

<i18n lang="json">
{
  "en": {
    "summary_empty": "Showing 0 {items}",
    "summary": "Showing {from}-{to} of {total} {items}",
    "previous": "Previous",
    "next": "Next",
    "per_page": "Per page"
  }
}
</i18n>
